import React, { Fragment, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import { currentPageAccess } from "../utils/library";

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const API_BASE = WEBSERVICE;

const apiGet = (url) =>
  fetch(url, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });

const apiPost = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then((r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function formatSchedule(schedules) {
  if (!schedules) return "Always";
  const { dayOfWeek, startTime, endTime } = schedules;
  const day =
    dayOfWeek != null && dayOfWeek >= 0 && dayOfWeek <= 6
      ? DAY_NAMES[dayOfWeek]
      : null;
  if (!day && !startTime) return "Always";
  if (day && startTime && endTime) return `${day} ${startTime}–${endTime}`;
  if (day) return day;
  return "Always";
}

function normalizeCampaign(c) {
  return {
    id: c.campaignId,
    name: c.campaignName,
    type: c.rewardType,
    rate: c.rewardRate,
    minPurchase: c.minPurchase,
    maxReward: c.maxReward,
    startDate: c.startDate ? c.startDate.split("T")[0] : "",
    endDate: c.endDate ? c.endDate.split("T")[0] : "",
    status: c.status?.toLowerCase() || "active",
    schedule: formatSchedule(c.schedules),
    schedules: c.schedules,
    scheduleId: c.schedules?.scheduleId || null,
    transactionCount: c.transactionCount || 0,
    totalCashbackEarned: c.totalCashbackEarned || 0,
    totalItemPrice: c.totalItemPrice || 0,
  };
}

// ─────────────────────────────────────────────────────────────────
// Style tokens
// ─────────────────────────────────────────────────────────────────
const CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "16px",
};

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        background: type === "success" ? "#10b981" : "#ef4444",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: "12px",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        animation: "slideUp .2s ease",
      }}
    >
      <i
        className={`mdi ${
          type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
        }`}
      />
      {msg}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active: { bg: "rgba(16,185,129,.12)", color: "#10b981", dot: true },
    scheduled: { bg: "rgba(245,158,11,.12)", color: "#f59e0b", dot: false },
    paused: { bg: "rgba(100,116,139,.15)", color: "#94a3b8", dot: false },
    ended: { bg: "rgba(239,68,68,.1)", color: "#ef4444", dot: false },
    draft: { bg: "rgba(148,163,184,.12)", color: "#94a3b8", dot: false },
    expired: { bg: "rgba(239,68,68,.1)", color: "#ef4444", dot: false },
  };
  const s = cfg[status] || cfg.paused;
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {s.dot && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: s.color,
            boxShadow: `0 0 6px ${s.color}`,
          }}
        />
      )}
      {label}
    </span>
  );
}

function FilterTab({ label, active, count, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: 8,
        border: active
          ? "1.5px solid var(--btn-color)"
          : "1.5px solid var(--body-color-less2)",
        background: active
          ? "color-mix(in srgb,var(--btn-color) 10%,transparent)"
          : "transparent",
        color: active ? "var(--btn-color)" : "var(--text-color)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        opacity: active ? 1 : 0.6,
        transition: "all .15s",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {label}
      {count != null && (
        <span
          style={{
            minWidth: 18,
            height: 18,
            borderRadius: 9,
            padding: "0 5px",
            background: active ? "var(--btn-color)" : "var(--body-color-less2)",
            color: active ? "#fff" : "var(--text-color)",
            fontSize: 10,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Delete Confirmation Modal
function DeleteModal({ isOpen, onClose, onConfirm, campaignName, deleting }) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "fadeIn .2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--body-color-less)",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "90%",
          padding: "28px 24px",
          boxShadow: "0 20px 35px -10px rgba(0,0,0,0.3)",
          animation: "slideUp .25s ease",
          border: "1px solid var(--body-color-less2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "28px",
            background: "rgba(239, 68, 68, 0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <i
            className="mdi mdi-trash-can-outline"
            style={{
              fontSize: "28px",
              color: "#ef4444",
            }}
          />
        </div>

        <h3
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "var(--text-color)",
            marginBottom: "8px",
            letterSpacing: "-0.3px",
          }}
        >
          Delete Campaign?
        </h3>

        <p
          style={{
            fontSize: "14px",
            color: "var(--text-color)",
            opacity: 0.6,
            marginBottom: "24px",
            lineHeight: 1.5,
          }}
        >
          Are you sure you want to delete{" "}
          <strong style={{ color: "#ef4444" }}>"{campaignName}"</strong>? This
          action cannot be undone and all campaign data will be permanently
          removed.
        </p>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: "1.5px solid var(--body-color-less2)",
              background: "transparent",
              color: "var(--text-color)",
              fontSize: "13px",
              fontWeight: "600",
              cursor: deleting ? "not-allowed" : "pointer",
              opacity: deleting ? 0.5 : 0.7,
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              if (!deleting) e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              if (!deleting) e.currentTarget.style.opacity = "0.7";
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              padding: "10px 24px",
              borderRadius: "10px",
              border: "none",
              background: "#ef4444",
              color: "#fff",
              fontSize: "13px",
              fontWeight: "600",
              cursor: deleting ? "not-allowed" : "pointer",
              opacity: deleting ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              if (!deleting) e.currentTarget.style.background = "#dc2626";
            }}
            onMouseLeave={(e) => {
              if (!deleting) e.currentTarget.style.background = "#ef4444";
            }}
          >
            {deleting ? (
              <>
                <i className="mdi mdi-loading mdi-spin" /> Deleting...
              </>
            ) : (
              <>
                <i className="mdi mdi-trash-can-outline" /> Delete Campaign
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Modal
function EditModal({ campaign, onClose, onSave }) {
  const [form, setForm] = useState({
    name: campaign.name,
    rate: campaign.rate,
    minPurchase: campaign.minPurchase || "",
    maxReward: campaign.maxReward || "",
    schedule: campaign.schedule,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--popupbody-color, var(--body-color-less))",
          border: "1px solid var(--body-color-less2)",
          borderRadius: 16,
          padding: 28,
          width: 440,
          maxWidth: "90vw",
          boxShadow: "0 20px 60px rgba(0,0,0,.4)",
          animation: "slideUp .2s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <h4
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--text-color)",
              margin: 0,
            }}
          >
            Edit Campaign
          </h4>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid var(--body-color-less2)",
              background: "transparent",
              cursor: "pointer",
              color: "var(--text-color)",
              fontSize: 16,
              opacity: 0.6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="mdi mdi-close" />
          </button>
        </div>

        {[
          {
            label: "Campaign Name",
            key: "name",
            type: "text",
            placeholder: "e.g. Weekend Cashback",
          },
          {
            label: "Reward Rate (%)",
            key: "rate",
            type: "number",
            placeholder: "5",
          },
          {
            label: "Min Purchase ($)",
            key: "minPurchase",
            type: "number",
            placeholder: "0",
          },
          {
            label: "Max Reward ($)",
            key: "maxReward",
            type: "number",
            placeholder: "0",
          },
          {
            label: "Schedule",
            key: "schedule",
            type: "text",
            placeholder: "Always",
          },
        ].map(({ label, key, type, placeholder }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".5px",
                color: "var(--text-color)",
                opacity: 0.4,
                marginBottom: 6,
                display: "block",
              }}
            >
              {label}
            </label>
            <input
              type={type}
              placeholder={placeholder}
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              style={{
                width: "100%",
                height: 38,
                padding: "0 12px",
                fontSize: 13,
                borderRadius: 8,
                border: "1.5px solid var(--body-color-less2)",
                background: "var(--body-color)",
                color: "var(--text-color)",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        ))}

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px",
              borderRadius: 9,
              border: "1px solid var(--body-color-less2)",
              background: "transparent",
              color: "var(--text-color)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            style={{
              padding: "9px 22px",
              borderRadius: 9,
              border: "none",
              background: "var(--btn-color)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
const STATUS_FILTERS = ["all", "active", "scheduled", "paused", "ended"];

export default function RewardCampaigns() {
  const history = useHistory();
  const isReadOnly = currentPageAccess() === "READ";
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState({ msg: "", type: "success" });
  const [expandedId, setExpandedId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, campaign: null, deleting: false });

  const showMsg = (type, msg) => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3000);
  };

  const fetchCampaigns = async (status) => {
    setLoading(true);
    const statusParam = status === "all" ? "all" : status;
    try {
      const res = await apiGet(
        `${API_BASE}/campaigns/merchant?merchantId=${getMerchantId()}&status=${statusParam}`
      );
      if (res.status && Array.isArray(res.data)) {
        setCampaigns(res.data.map(normalizeCampaign));
      } else {
        setCampaigns([]);
      }
    } catch {
      showMsg("error", "Failed to load campaigns");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns(filter);
  }, [filter]);

  const handlePause = async (c) => {
    if (isReadOnly) {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    const isPaused = c.status === "paused";
    const endpoint = isPaused ? "activate" : "pause";
    try {
      const res = await apiPost(`${API_BASE}/campaigns/${c.id}/${endpoint}`, {});
      if (res.status) {
        showMsg(
          "success",
          isPaused ? `"${c.name}" activated` : `"${c.name}" paused`
        );
        fetchCampaigns(filter);
      } else {
        showMsg("error", res.message || "Operation failed");
      }
    } catch {
      showMsg("error", "Request failed. Please try again.");
    }
  };

  const handleEdit = (campaign) => {
    if (isReadOnly) {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    history.push(`/rewards/edit/${campaign.id}`);
  };

  const handleSaveEdit = (form) => {
    setCampaigns((prev) =>
      prev.map((x) =>
        x.id === editTarget.id
          ? {
              ...x,
              name: form.name,
              rate: parseFloat(form.rate),
              minPurchase: parseFloat(form.minPurchase) || 0,
              maxReward: parseFloat(form.maxReward) || 0,
              schedule: form.schedule,
            }
          : x
      )
    );
    showMsg("success", "Campaign updated");
    setEditTarget(null);
  };

  const handleDeleteClick = (campaign) => {
    if (isReadOnly) {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    setDeleteModal({ isOpen: true, campaign, deleting: false });
  };

  const handleDeleteConfirm = async () => {
    const { campaign } = deleteModal;
    if (!campaign) return;

    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    try {
      const payload = {
        merchantId: getMerchantId(),
        campaignId: campaign.id,
      };

      // Include scheduleId if it exists
      if (campaign.scheduleId !== null && campaign.scheduleId !== undefined) {
        payload.scheduleId = campaign.scheduleId;
      }

      const res = await fetch(`${API_BASE}/campaigns/createreward?action=DELETE`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      }).then((r) => {
        if (!r.ok) throw new Error("Request failed");
        return r.json();
      });

      if (res.status) {
        showMsg("success", `"${campaign.name}" deleted`);
        setCampaigns((prev) => prev.filter((x) => x.id !== campaign.id));
        setDeleteModal({ isOpen: false, campaign: null, deleting: false });
        // Close expanded row if it was open
        if (expandedId === campaign.id) {
          setExpandedId(null);
        }
      } else {
        showMsg("error", res.message || "Delete failed");
        setDeleteModal(prev => ({ ...prev, deleting: false }));
      }
    } catch {
      showMsg("error", "Request failed. Please try again.");
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, campaign: null, deleting: false });
  };

  // Search filter only — status filtering is done server-side
  const filtered = campaigns.filter(
    (c) => !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  const countFor = (s) => (s === filter ? campaigns.length : null);

  return (
    <Fragment>
      <style>{`
        @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes shimmer{0%{opacity:.4}50%{opacity:.8}100%{opacity:.4}}
        .rc-row:hover .rc-actions{opacity:1!important;}
        .rc-table-header{display:grid;grid-template-columns:2fr 110px 90px 80px 100px 100px 110px 40px;gap:12px;padding:8px 12px;margin-bottom:4px;}
        .rc-row{display:grid;grid-template-columns:2fr 110px 90px 80px 100px 100px 110px 40px;gap:12px;padding:12px 12px;border-radius:10px;align-items:center;cursor:default;transition:background .15s,border-color .15s;}
        @media(max-width:768px){
          .rc-table-header{display:none!important;}
          .rc-row{display:flex!important;flex-wrap:wrap!important;gap:8px 16px!important;padding:14px 12px!important;}
          .rc-row>div{min-width:0;}
          .rc-cell-name{flex:1 1 100%!important;margin-bottom:4px;}
          .rc-cell-hide-mobile{display:none!important;}
          .rc-cell-expand{margin-left:auto;}
        }
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={{ padding: "32px 24px 60px" }}>
            <Toast msg={toast.msg} type={toast.type} />
            
            {/* Delete Modal */}
            <DeleteModal
              isOpen={deleteModal.isOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              campaignName={deleteModal.campaign?.name || ""}
              deleting={deleteModal.deleting}
            />
            
            {editTarget && (
              <EditModal
                campaign={editTarget}
                onClose={() => setEditTarget(null)}
                onSave={handleSaveEdit}
              />
            )}

            {/* ── Header ── */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <button
                    onClick={() => history.push("/rewards")}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      border: "1px solid var(--body-color-less2)",
                      background: "transparent",
                      color: "var(--text-color)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: 16,
                      opacity: 0.6,
                    }}
                  >
                    <i className="mdi mdi-arrow-left" />
                  </button>
                  <h2
                    style={{
                      fontSize: "22px",
                      fontWeight: "700",
                      color: "var(--text-color)",
                      margin: 0,
                      letterSpacing: "-.3px",
                    }}
                  >
                    Reward Campaigns
                  </h2>
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--text-color)",
                    opacity: 0.45,
                    margin: "0 0 0 40px",
                  }}
                >
                  Manage all your reward campaigns in one place
                </p>
              </div>
              {!isReadOnly && (
                <button
                  onClick={() => history.push("/rewards/create")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 20px",
                    borderRadius: 10,
                    border: "none",
                    background: "var(--btn-color)",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow:
                      "0 0 14px color-mix(in srgb,var(--btn-color) 30%,transparent)",
                  }}
                >
                  <i className="mdi mdi-plus" style={{ fontSize: 16 }} />
                  Create Campaign
                </button>
              )}
            </div>

            {/* ── Filters + Search ── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {STATUS_FILTERS.map((s) => (
                  <FilterTab
                    key={s}
                    label={s.charAt(0).toUpperCase() + s.slice(1)}
                    active={filter === s}
                    count={loading ? null : (s === "all" ? campaigns.length : countFor(s))}
                    onClick={() => setFilter(s)}
                  />
                ))}
              </div>
              <div style={{ position: "relative" }}>
                <i
                  className="mdi mdi-magnify"
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 15,
                    color: "var(--text-color)",
                    opacity: 0.35,
                  }}
                />
                <input
                  type="text"
                  placeholder="Search campaigns…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    height: 34,
                    padding: "0 12px 0 32px",
                    borderRadius: 8,
                    fontSize: 13,
                    border: "1.5px solid var(--body-color-less2)",
                    background: "var(--body-color)",
                    color: "var(--text-color)",
                    outline: "none",
                    width: 200,
                  }}
                />
              </div>
            </div>

            {/* ── Table ── */}
            <div style={CARD}>
              {/* Table header */}
              <div className="rc-table-header">
                {[
                  "Campaign Name",
                  "Type",
                  "Rate",
                  "Min Buy",
                  "Max Reward",
                  "Schedule",
                  "Status",
                  "",
                ].map((h) => (
                  <div
                    key={h}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".6px",
                      color: "var(--text-color)",
                      opacity: 0.35,
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {loading ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: 60,
                        borderRadius: 10,
                        background: "var(--body-color-less2)",
                        animation: "shimmer 1.4s ease infinite",
                      }}
                    />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div
                  style={{
                    padding: "48px 16px",
                    textAlign: "center",
                    border: "1.5px dashed var(--body-color-less2)",
                    borderRadius: 10,
                  }}
                >
                  <i
                    className="mdi mdi-gift"
                    style={{
                      fontSize: 36,
                      color: "var(--text-color)",
                      opacity: 0.15,
                      display: "block",
                      marginBottom: 12,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--text-color)",
                      opacity: 0.4,
                      marginBottom: 8,
                    }}
                  >
                    {search
                      ? "No campaigns match your search"
                      : "No campaigns yet"}
                  </div>
                  {!search && !isReadOnly && (
                    <button
                      onClick={() => history.push("/rewards/create")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 18px",
                        borderRadius: 9,
                        border: "none",
                        background: "var(--btn-color)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <i className="mdi mdi-plus" />
                      Create your first campaign
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  {filtered.map((c) => {
                    const isExpanded = expandedId === c.id;
                    const isActive = c.status === "active";
                    return (
                      <div key={c.id}>
                        <div
                          className="rc-row"
                          style={{
                            background: isExpanded
                              ? "color-mix(in srgb,var(--btn-color) 5%,transparent)"
                              : "transparent",
                            border: isExpanded
                              ? "1px solid color-mix(in srgb,var(--btn-color) 20%,transparent)"
                              : "1px solid transparent",
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded)
                              e.currentTarget.style.background =
                                "var(--body-color-less2)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded)
                              e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {/* Name */}
                          <div className="rc-cell-name">
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--text-color)",
                                marginBottom: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.name}
                            </div>
                            {(c.totalCashbackEarned > 0 || c.transactionCount > 0) && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#10b981",
                                  opacity: 0.8,
                                }}
                              >
                                ${c.totalCashbackEarned} rewards · {c.transactionCount} transactions
                              </div>
                            )}
                          </div>

                          {/* Type */}
                          <div>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 5,
                                background:
                                  c.type === "cashback"
                                    ? "rgba(99,102,241,.12)"
                                    : "rgba(20,184,166,.12)",
                                color:
                                  c.type === "cashback" ? "#818cf8" : "#2dd4bf",
                              }}
                            >
                              {c.type === "cashback"
                                ? "Cashback"
                                : "Store Credit"}
                            </span>
                          </div>

                          {/* Rate */}
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "var(--text-color)",
                            }}
                          >
                            {c.rate}%
                          </div>

                          {/* Min */}
                          <div
                            className="rc-cell-hide-mobile"
                            style={{
                              fontSize: 12,
                              color: "var(--text-color)",
                              opacity: 0.55,
                            }}
                          >
                            {c.minPurchase ? `$${c.minPurchase}` : "—"}
                          </div>

                          {/* Max */}
                          <div
                            className="rc-cell-hide-mobile"
                            style={{
                              fontSize: 12,
                              color: "var(--text-color)",
                              opacity: 0.55,
                            }}
                          >
                            {c.maxReward ? `$${c.maxReward}` : "—"}
                          </div>

                          {/* Schedule */}
                          <div
                            className="rc-cell-hide-mobile"
                            style={{
                              fontSize: 12,
                              color: "var(--text-color)",
                              opacity: 0.6,
                            }}
                          >
                            {c.schedule}
                          </div>

                          {/* Status */}
                          <div>
                            <StatusBadge status={c.status} />
                          </div>

                          {/* Actions */}
                          <div
                            className="rc-actions rc-cell-expand"
                            style={{
                              display: "flex",
                              gap: 4,
                              opacity: 0.4,
                              transition: "opacity .15s",
                            }}
                          >
                            <div style={{ position: "relative" }}>
                              <button
                                onClick={() =>
                                  setExpandedId(isExpanded ? null : c.id)
                                }
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 7,
                                  border: "1px solid var(--body-color-less2)",
                                  background: isExpanded
                                    ? "var(--btn-color)"
                                    : "transparent",
                                  color: isExpanded
                                    ? "#fff"
                                    : "var(--text-color)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: 14,
                                }}
                              >
                                <i
                                  className={`mdi mdi-chevron-${
                                    isExpanded ? "up" : "down"
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Expanded detail row */}
                        {isExpanded && (
                          <div
                            style={{
                              margin: "0 4px 4px",
                              padding: "16px 16px",
                              borderRadius: "0 0 10px 10px",
                              background:
                                "color-mix(in srgb,var(--btn-color) 4%,transparent)",
                              border:
                                "1px solid color-mix(in srgb,var(--btn-color) 14%,transparent)",
                              borderTop: "none",
                              animation: "slideUp .18s ease",
                            }}
                          >
                            {/* Stats row */}
                            <div
                              style={{
                                display: "flex",
                                gap: 20,
                                flexWrap: "wrap",
                                marginBottom: 14,
                              }}
                            >
                              {[
                                {
                                  label: "Total Volume",
                                  value: `$${c.totalItemPrice || 0}`,
                                  color: "#10b981",
                                },
                                {
                                  label: "Total Rewards",
                                  value: `$${c.totalCashbackEarned || 0}`,
                                  color: "var(--btn-color)",
                                },
                                {
                                  label: "Transactions",
                                  value: c.transactionCount || 0,
                                  color: "#8b5cf6",
                                },
                              ].map(({ label, value, color }) => (
                                <div key={label}>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      letterSpacing: ".6px",
                                      color: "var(--text-color)",
                                      opacity: 0.35,
                                      marginBottom: 3,
                                    }}
                                  >
                                    {label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 18,
                                      fontWeight: 700,
                                      color,
                                      letterSpacing: "-.3px",
                                    }}
                                  >
                                    {value}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Action buttons */}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                onClick={() => handleEdit(c)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  padding: "6px 14px",
                                  borderRadius: 8,
                                  border: "1.5px solid var(--body-color-less2)",
                                  background: "transparent",
                                  color: "var(--text-color)",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                <i
                                  className="mdi mdi-pencil"
                                  style={{ fontSize: 13 }}
                                />
                                Edit Campaign
                              </button>

                              {c.status !== "ended" && (
                                <button
                                  onClick={() => handlePause(c)}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "6px 14px",
                                    borderRadius: 8,
                                    border: `1.5px solid ${
                                      c.status === "paused"
                                        ? "rgba(16,185,129,.3)"
                                        : "rgba(245,158,11,.3)"
                                    }`,
                                    background:
                                      c.status === "paused"
                                        ? "rgba(16,185,129,.08)"
                                        : "rgba(245,158,11,.08)",
                                    color:
                                      c.status === "paused"
                                        ? "#10b981"
                                        : "#f59e0b",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  <i
                                    className={`mdi ${
                                      c.status === "paused"
                                        ? "mdi-play"
                                        : "mdi-pause-circle"
                                    }`}
                                    style={{ fontSize: 13 }}
                                  />
                                  {c.status === "paused" ? "Activate" : "Pause"}
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteClick(c)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  padding: "6px 14px",
                                  borderRadius: 8,
                                  border: "1.5px solid rgba(239,68,68,.25)",
                                  background: "rgba(239,68,68,.07)",
                                  color: "#ef4444",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                <i
                                  className="mdi mdi-trash-can"
                                  style={{ fontSize: 13 }}
                                />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}